import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, CornerDownLeft, Plus, Target, ClipboardCheck, Sparkles, LayoutDashboard, ListChecks,
  MessageCircle, CalendarDays, Settings, Users, User, CheckCircle2, Clock, History, Smartphone, TrendingUp,
} from "lucide-react";
import { api } from "../lib/api";
import { dueShort } from "../lib/taskActions";

const PAGES = [
  { label: "Dashboard", to: "/app/dashboard", icon: LayoutDashboard },
  { label: "Tasks", to: "/app/tasks", icon: ListChecks },
  { label: "Chats", to: "/app/messages", icon: MessageCircle },
  { label: "Review Queue", to: "/app/review", icon: ClipboardCheck },
  { label: "Calendar", to: "/app/calendar", icon: CalendarDays },
  { label: "Leads", to: "/app/leads", icon: TrendingUp },
  { label: "Stream History", to: "/app/stream-history", icon: History },
  { label: "WhatsApp Connect", to: "/app/whatsapp", icon: Smartphone },
  { label: "Settings", to: "/app/settings", icon: Settings },
  { label: "Team", to: "/app/settings/team", icon: Users },
];

const CACHE_MS = 60000;
let cache = { at: 0, tasks: [], chats: [] };

// Every whitespace-separated term must appear somewhere in the haystack.
const matches = (hay, q) => {
  const h = hay.toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => h.includes(w));
};

// ⌘K / Ctrl+K anywhere in the app. Also opens on the "brainydo:command-palette"
// window event (dashboard Search button, top-bar search box).
export default function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [data, setData] = useState({ tasks: [], chats: [] });
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("brainydo:command-palette", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("brainydo:command-palette", onOpen); };
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ(""); setSel(0);
    if (Date.now() - cache.at < CACHE_MS) { setData(cache); return; }
    setLoading(true);
    Promise.all([
      api.get("/tasks", { params: { in_review: false } }).then((r) => r.data.tasks || []).catch(() => []),
      api.get("/chats", { params: { limit: 300 } }).then((r) => r.data.chats || []).catch(() => []),
    ]).then(([tasks, chats]) => {
      cache = { at: Date.now(), tasks, chats };
      setData(cache);
    }).finally(() => setLoading(false));
  }, [open]);

  const close = () => setOpen(false);
  const go = (to) => { close(); nav(to); };
  const fire = (evt) => { close(); setTimeout(() => window.dispatchEvent(new CustomEvent(evt)), 50); };

  const results = useMemo(() => {
    const actions = [
      { kind: "action", label: "New task", icon: Plus, run: () => go("/app/tasks?create=1"), keys: "new create add task" },
      { kind: "action", label: "Start focus mode", icon: Target, run: () => fire("brainydo:focus-mode"), keys: "focus overdue today triage" },
      { kind: "action", label: "Review queue", icon: ClipboardCheck, run: () => go("/app/review"), keys: "review approve" },
      { kind: "action", label: "What needs attention", icon: Clock, run: () => go("/app/tasks?bucket=attention"), keys: "overdue today attention urgent" },
    ];
    const pages = PAGES.map((p) => ({ kind: "page", label: p.label, icon: p.icon, run: () => go(p.to), keys: p.label }));
    if (!q.trim()) {
      const active = data.tasks.filter((t) => t.status === "pending" || t.status === "in_progress")
        .sort((a, b) => (a.deadline || "9") < (b.deadline || "9") ? -1 : 1).slice(0, 5);
      return [
        { title: "Quick actions", items: actions },
        { title: "Next up", items: active.map(taskItem) },
        { title: "Go to", items: pages.slice(0, 6) },
      ].filter((g) => g.items.length);
    }
    const qq = q.trim();
    const byNo = qq.match(/^#?(\d+)$/);
    const tasks = data.tasks.filter((t) =>
      (byNo && String(t.task_no) === byNo[1]) ||
      matches(`${t.title} ${t.summary || ""} ${t.sender_name || ""} ${t.chat_name || ""} ${t.assignee || ""}`, qq))
      .sort((a, b) => rank(a) - rank(b)).slice(0, 7);
    const chats = data.chats.filter((c) => matches(`${c.chat_name || ""} ${c.chat_phone || ""}`, qq)).slice(0, 5);
    return [
      { title: "Actions", items: [...actions, ...pages].filter((a) => matches(`${a.label} ${a.keys}`, qq)).slice(0, 4) },
      { title: "Tasks", items: tasks.map(taskItem) },
      { title: "Chats & people", items: chats.map((c) => ({
        kind: "chat", label: c.chat_name || c.chat_phone || c.chat_id, icon: c.chat_type === "group" ? Users : User,
        sub: c.last_text || c.text || "", run: () => go(`/app/messages?chat=${encodeURIComponent(c.chat_id)}`),
      })) },
      { title: "Ask Brainydo", items: [{ kind: "ask", label: `Ask Brainydo: “${qq}”`, icon: Sparkles, run: () => { go("/app/dashboard"); askLater(qq); } }] },
    ].filter((g) => g.items.length);

    function taskItem(t) {
      const due = t.status === "completed" ? { text: "done", tone: "slate" } : dueShort(t.deadline);
      return {
        kind: "task", label: t.title, icon: t.status === "completed" ? CheckCircle2 : ListChecks,
        prefix: t.task_no ? `#${t.task_no}` : null, sub: t.sender_name || t.chat_name || "",
        badge: due, run: () => go(`/app/tasks?focus=${t.id}`),
      };
    }
    function rank(t) { return (t.status === "pending" || t.status === "in_progress") ? 0 : 1; }
  }, [q, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = results.flatMap((g) => g.items);
  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${sel}"]`)?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  if (!open) return null;

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(flat.length - 1, s + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); flat[sel]?.run(); }
    else if (e.key === "Escape") close();
  };

  let idx = -1;
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-[2px] flex items-start justify-center p-4 pt-[12vh] sw-fade-in" onMouseDown={close} data-testid="command-palette">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden sw-pop" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 border-b border-slate-100">
          <Search size={18} className="text-slate-400 shrink-0" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Search tasks, chats, people, #12… or type a command"
            className="flex-1 h-14 text-[15px] outline-none bg-transparent placeholder:text-slate-400" data-testid="command-input" />
          <kbd className="text-[10.5px] font-mono text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
          {loading && !flat.length && <div className="px-4 py-6 text-center text-[13px] text-slate-400">Loading…</div>}
          {!loading && !flat.length && <div className="px-4 py-6 text-center text-[13px] text-slate-400">No matches</div>}
          {results.map((g) => (
            <div key={g.title} className="mb-1">
              <div className="px-4 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-400">{g.title}</div>
              {g.items.map((it) => {
                idx += 1;
                const i = idx;
                const Icon = it.icon;
                const on = i === sel;
                return (
                  <button key={`${g.title}-${i}`} data-idx={i} onMouseMove={() => setSel(i)} onClick={() => it.run()}
                    className={"w-full flex items-center gap-3 px-4 py-2 text-left transition-colors " + (on ? "bg-blue-50" : "")}>
                    <span className={"w-7 h-7 rounded-lg grid place-items-center shrink-0 " + (on ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500")}><Icon size={14} /></span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5 min-w-0">
                        {it.prefix && <span className="font-mono text-[11px] text-slate-400 shrink-0">{it.prefix}</span>}
                        <span className="text-[13.5px] font-medium text-slate-900 truncate">{it.label}</span>
                      </span>
                      {it.sub && <span className="block text-[11.5px] text-slate-500 truncate">{it.sub}</span>}
                    </span>
                    {it.badge && <span className={"text-[11px] font-semibold shrink-0 " + (it.badge.tone === "red" ? "text-red-600" : it.badge.tone === "amber" ? "text-amber-700" : "text-slate-400")}>{it.badge.text}</span>}
                    {on && <CornerDownLeft size={13} className="text-blue-500 shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/70 flex items-center gap-4 text-[11px] text-slate-400">
          <span><kbd className="font-mono">↑↓</kbd> move</span>
          <span><kbd className="font-mono">↵</kbd> open</span>
          <span className="ml-auto"><kbd className="font-mono">⌘K</kbd> anywhere</span>
        </div>
      </div>
    </div>
  );
}

// Hand a question to the dashboard's Ask Brainydo box once it has rendered.
function askLater(question) {
  let tries = 0;
  const id = setInterval(() => {
    tries += 1;
    const input = document.querySelector('[data-testid="ask-brainydo-input"]');
    if (!input) {
      if (tries === 3) document.querySelector('[data-testid="ask-brainydo-toggle"]')?.click();
      if (tries > 20) clearInterval(id);
      return;
    }
    clearInterval(id);
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
    setter.call(input, question);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.scrollIntoView({ behavior: "smooth", block: "center" });
    input.focus();
  }, 150);
}
